import React, { useState, useMemo, useEffect, useCallback } from 'react';
import moment from 'moment';
import { Chart as ChartJS, ArcElement, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';

ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, Tooltip, Legend);

export default function ConsumptionTab({
  unit = 'm³',
  fromDate,
  toDate,
  onFromDateChange,
  onToDateChange,
  onExport,
  dailyLogs = [],
  rawTelemetry = [],
  onDownloadTable,
  internalLimit = 60,
  complianceLimit = 80,
  pumpRecords = [],
  selectedMeter,
  baseUrl,
  Token,
  axiosInstance,
}) {
  // 1. Daily Consumption Pagination (10 records / page)
  const [dailyPage, setDailyPage] = useState(1);
  const dailyPageSize = 10;
  const totalDailyPages = Math.max(1, Math.ceil(dailyLogs.length / dailyPageSize));
  const displayedDaily = dailyLogs.slice((dailyPage - 1) * dailyPageSize, dailyPage * dailyPageSize);

  useEffect(() => {
    setDailyPage(1);
  }, [fromDate, toDate]);

  // 2. Raw Telemetry Server-Side Pagination (10 records / page)
  const [telemetryInterval, setTelemetryInterval] = useState('1h');
  const [telemetryLogs, setTelemetryLogs] = useState([]);
  const [loadingTelemetry, setLoadingTelemetry] = useState(false);
  const [rawPage, setRawPage] = useState(1);
  const [totalRawPages, setTotalRawPages] = useState(1);
  const [totalRawItems, setTotalRawItems] = useState(0);
  const rawPageSize = 10;

  const intervalHours = useMemo(() => {
    switch (telemetryInterval) {
      case '15m': return 0.25;
      case '30m': return 0.5;
      case '1h': return 1;
      case '4h': return 4;
      case '8h': return 8;
      case '12h': return 12;
      case '1d': return 24;
      default: return 1;
    }
  }, [telemetryInterval]);

  const fetchTelemetryLogs = useCallback(async (targetPage = 1) => {
    if (!selectedMeter || selectedMeter === 'All' || !baseUrl) return;
    setLoadingTelemetry(true);
    try {
      const client = axiosInstance || null;
      const headers = Token ? { Authorization: `Bearer ${Token}` } : {};
      const url = `${baseUrl}/device-readings/graph?productId=${selectedMeter}&startDate=${fromDate}&endDate=${toDate}&interval=${telemetryInterval}&page=${targetPage}&limit=${rawPageSize}&order=desc`;

      let resData = null;
      if (client) {
        const res = await client.get(url, { headers }).catch(() => null);
        resData = res?.data;
      } else {
        const res = await fetch(url, { headers }).catch(() => null);
        if (res && res.ok) {
          resData = await res.json();
        }
      }

      const items = resData?.data?.data || resData?.data;
      if (Array.isArray(items)) {
        const mapped = items.map((item) => {
          const usageVal = Number((Number(item.usage) || 0).toFixed(2));
          const dateStr = item.date ? moment(item.date).format('YYYY-MM-DD') : '';
          const startStr =
            telemetryInterval === '1d'
              ? `${dateStr} 00:00`
              : `${dateStr} ${item.startTime && item.startTime !== '-' ? item.startTime : '00:00'}`;
          const endStr =
            telemetryInterval === '1d'
              ? `${dateStr} 23:59`
              : `${dateStr} ${item.endTime && item.endTime !== '-' ? item.endTime : '23:59'}`;

          const openingVal =
            item.openingValue !== null && item.openingValue !== undefined
              ? Number(item.openingValue).toFixed(2)
              : '—';
          const closingVal =
            item.closingValue !== null && item.closingValue !== undefined
              ? Number(item.closingValue).toFixed(2)
              : '—';

          return {
            startTime: startStr,
            endTime: endStr,
            opening: openingVal,
            closing: closingVal,
            net: usageVal.toFixed(2),
            usageVal: usageVal,
            pump: usageVal > 0 ? 'Active' : 'Idle',
          };
        });
        setTelemetryLogs(mapped);
        const pagination = resData?.data?.pagination || resData?.pagination;
        setTotalRawPages(pagination?.totalPages || 1);
        setTotalRawItems(pagination?.totalItems !== undefined ? pagination.totalItems : mapped.length);
      } else {
        setTelemetryLogs([]);
        setTotalRawPages(1);
        setTotalRawItems(0);
      }
    } catch (e) {
      console.warn('Failed to fetch telemetry logs:', e);
      setTelemetryLogs([]);
      setTotalRawPages(1);
      setTotalRawItems(0);
    } finally {
      setLoadingTelemetry(false);
    }
  }, [selectedMeter, baseUrl, Token, axiosInstance, fromDate, toDate, telemetryInterval, rawPageSize]);

  useEffect(() => {
    setRawPage(1);
  }, [selectedMeter, fromDate, toDate, telemetryInterval]);

  useEffect(() => {
    fetchTelemetryLogs(rawPage);
  }, [rawPage, selectedMeter, fromDate, toDate, telemetryInterval]);

  // Dynamically compute shift water volume from real daily logs and pump shift distributions
  const { shiftData, shiftTotal } = useMemo(() => {
    const total = dailyLogs.reduce((acc, r) => acc + (Number(r.v) || 0), 0);

    let aHours = 0,
      bHours = 0,
      cHours = 0;

    if (Array.isArray(pumpRecords) && pumpRecords.length > 0) {
      pumpRecords.forEach((pr) => {
        aHours += pr.rawA !== undefined ? Number(pr.rawA) : 0;
        bHours += pr.rawB !== undefined ? Number(pr.rawB) : 0;
        cHours += pr.rawC !== undefined ? Number(pr.rawC) : 0;
      });
    }

    const totalHours = aHours + bHours + cHours;
    let shiftA, shiftB, shiftC;

    if (totalHours > 0) {
      // Allocate water volume proportionally to real 8-hour shift running durations
      shiftA = Number((total * (aHours / totalHours)).toFixed(2));
      shiftB = Number((total * (bHours / totalHours)).toFixed(2));
      shiftC = Number(Math.max(0, total - shiftA - shiftB).toFixed(2));
    } else {
      // Equal 8-hour split fallback (1/3 each)
      shiftA = Number((total / 3).toFixed(2));
      shiftB = Number((total / 3).toFixed(2));
      shiftC = Number(Math.max(0, total - shiftA - shiftB).toFixed(2));
    }

    return {
      shiftTotal: total.toFixed(2),
      shiftData: {
        labels: ['Shift A (12 AM–8 AM)', 'Shift B (8 AM–4 PM)', 'Shift C (4 PM–12 AM)'],
        datasets: [
          {
            data: total > 0 ? [shiftA, shiftB, shiftC] : [0, 0, 0],
            backgroundColor: ['#ef6b6f', '#1072bb', '#2ec98a'],
            borderWidth: 2,
            borderColor: '#fff',
          },
        ],
      },
    };
  }, [dailyLogs, pumpRecords]);

  const shiftOptions = {
    cutout: '70%',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: { color: '#5b7482', boxWidth: 10, font: { size: 11 }, padding: 12 },
      },
      tooltip: {
        backgroundColor: '#0b2e45',
        callbacks: {
          label: (c) => ` ${c.label}: ${c.raw} ${unit}`,
        },
      },
    },
  };

  // Dynamically compute monthly bar data from real logs
  const monthlyData = useMemo(() => {
    const monthMap = {};
    dailyLogs.forEach((r) => {
      const parts = (r.s || '').split(' ')[0].split(/[-/]/);
      let mKey = 'Current';
      if (parts.length >= 3) {
        if (parts[0].length === 4) {
          const mIdx = parseInt(parts[1], 10) - 1;
          const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          mKey = months[mIdx] || 'Current';
        } else {
          const mIdx = parseInt(parts[1], 10) - 1;
          const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          mKey = months[mIdx] || 'Current';
        }
      }
      monthMap[mKey] = (monthMap[mKey] || 0) + (Number(r.v) || 0);
    });

    const labels = Object.keys(monthMap).length > 0 ? Object.keys(monthMap) : ['Current Range'];
    const data = Object.keys(monthMap).length > 0 ? Object.values(monthMap).map((v) => Number(v.toFixed(2))) : [Number(shiftTotal)];

    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor: '#1072bb',
          borderRadius: 6,
          barThickness: 20,
        },
      ],
    };
  }, [dailyLogs, shiftTotal]);

  const monthlyOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#0b2e45',
        callbacks: {
          label: (c) => ` ${c.raw} ${unit}`,
        },
      },
    },
    scales: {
      x: {
        grid: { color: '#eef2f5' },
        ticks: { color: '#8aa1ad', font: { size: 11 } },
        border: { display: false },
      },
      y: {
        beginAtZero: true,
        grid: { color: '#eef2f5' },
        ticks: { color: '#8aa1ad', font: { size: 11 } },
        border: { display: false },
      },
    },
  };

  const activeRawList = telemetryLogs.length > 0 ? telemetryLogs : rawTelemetry;

  return (
    <div className="pane active">
      <div className="pane-tools">
        <h2>Water usage report</h2>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <span className="rlab">From</span>
          <input
            type="date"
            className="rng-from"
            value={fromDate}
            onChange={(e) => onFromDateChange(e.target.value)}
          />
          <span className="rlab">To</span>
          <input
            type="date"
            className="rng-to"
            value={toDate}
            onChange={(e) => onToDateChange(e.target.value)}
          />
          <button className="btn xls" onClick={onExport}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <path d="M14 2v6h6" />
            </svg>
            Excel
          </button>
        </div>
      </div>

      {/* Row of 2 charts */}
      <div className="row two-eq" style={{ marginBottom: '16px' }}>
        <div className="client-card">
          <div className="card-h">
            <h3>Shift split</h3>
            <span className="sub">selected range</span>
          </div>
          <div className="pad">
            <div className="donut-box">
              <Doughnut data={shiftData} options={shiftOptions} />
              <div className="donut-center has-legend">
                <b>{shiftTotal}</b>
                <small>{unit} total</small>
              </div>
            </div>
          </div>
        </div>

        <div className="client-card">
          <div className="card-h">
            <h3>Volume usage</h3>
            <span className="sub">selected period</span>
          </div>
          <div className="pad">
            <div className="chart-box chart-sm">
              <Bar data={monthlyData} options={monthlyOptions} />
            </div>
          </div>
        </div>
      </div>

      {/* Row of 2 tables */}
      <div className="row two-eq">
        {/* Daily consumption log */}
        <div className="client-card">
          <div className="card-h">
            <h3>Daily consumption log</h3>
            <div className="ch-right">
              <span className="sub">selected range</span>
              <div className="btnrow">
                <button
                  className="icon-dl xls"
                  title="Download Excel"
                  onClick={() => onDownloadTable('daily-consumption', 'xlsx')}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <path d="M14 2v6h6" />
                    <path d="M9.5 13l5 5M14.5 13l-5 5" />
                  </svg>
                </button>
                <button
                  className="icon-dl pdf"
                  title="Download PDF"
                  onClick={() => onDownloadTable('daily-consumption', 'pdf')}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <path d="M14 2v6h6" />
                    <path d="M9 14h6M9 17.5h4" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
          <div className="tbl-wrap">
            <table>
              <thead>
                <tr>
                  <th>Start time</th>
                  <th>End time</th>
                  <th>Flow status</th>
                  <th>Avg flow ({unit}/hr)</th>
                  <th>Consumption ({unit})</th>
                  <th>Reverse ({unit})</th>
                  <th>Net ({unit})</th>
                </tr>
              </thead>
              <tbody>
                {displayedDaily.length > 0 ? (
                  displayedDaily.map((r, i) => {
                    const isBreach = complianceLimit > 0 && r.v > complianceLimit;
                    const isWarn = internalLimit > 0 && r.v > internalLimit;
                    return (
                      <tr key={i}>
                        <td>{r.s}</td>
                        <td>{r.e}</td>
                        <td>{r.dur}</td>
                        <td>{Number(r.avg).toFixed(2)}</td>
                        <td className={isBreach ? 'c-breach' : isWarn ? 'c-warn' : 'c-ok'}>
                          {Number(r.v).toFixed(2)}
                        </td>
                        <td>{Number(r.rev || 0).toFixed(2)}</td>
                        <td>{Number(r.net || r.v).toFixed(2)}</td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="7" style={{ textAlign: 'center', color: '#8aa1ad', padding: '24px' }}>
                      No consumption records found for selected date range
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {dailyLogs.length > 0 && (
            <div className="tbl-foot">
              <span>
                {(dailyPage - 1) * dailyPageSize + 1}–{Math.min(dailyPage * dailyPageSize, dailyLogs.length)} of{' '}
                {dailyLogs.length}
              </span>
              <div className="pager">
                <button disabled={dailyPage <= 1} onClick={() => setDailyPage((p) => Math.max(1, p - 1))}>
                  ‹
                </button>
                <span>
                  Page {dailyPage} of {totalDailyPages}
                </span>
                <button disabled={dailyPage >= totalDailyPages} onClick={() => setDailyPage((p) => Math.min(totalDailyPages, p + 1))}>
                  ›
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Raw telemetry */}
        <div className="client-card">
          <div className="card-h">
            <h3>Raw telemetry</h3>
            <div className="ch-right" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <select
                className="telemetry-interval-select"
                value={telemetryInterval}
                onChange={(e) => {
                  setTelemetryInterval(e.target.value);
                  setRawPage(1);
                }}
                style={{
                  background: '#fff',
                  border: '1px solid #dbe4ea',
                  borderRadius: '8px',
                  padding: '4px 10px',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#0d3b54',
                  cursor: 'pointer',
                  outline: 'none',
                }}
              >
                <option value="15m">15 Minutes</option>
                <option value="30m">30 Minutes</option>
                <option value="1h">1 Hour</option>
                <option value="4h">4 Hours</option>
                <option value="8h">8 Hours</option>
                <option value="12h">12 Hours</option>
                <option value="1d">1 Day</option>
              </select>
              <div className="btnrow">
                <button
                  className="icon-dl xls"
                  title="Download Excel"
                  onClick={() => onDownloadTable('raw-telemetry', 'xlsx', telemetryInterval)}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <path d="M14 2v6h6" />
                    <path d="M9.5 13l5 5M14.5 13l-5 5" />
                  </svg>
                </button>
                <button
                  className="icon-dl pdf"
                  title="Download PDF"
                  onClick={() => onDownloadTable('raw-telemetry', 'pdf', telemetryInterval)}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <path d="M14 2v6h6" />
                    <path d="M9 14h6M9 17.5h4" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
          <div className="tbl-wrap">
            <table>
              <thead>
                <tr>
                  <th>Start time</th>
                  <th>Opening totalizer ({unit})</th>
                  <th>End time</th>
                  <th>Closing totalizer ({unit})</th>
                  <th>Net ({unit})</th>
                  <th>Pump status</th>
                </tr>
              </thead>
              <tbody>
                {loadingTelemetry ? (
                  <tr>
                    <td colSpan="6" style={{ textAlign: 'center', color: '#8aa1ad', padding: '24px' }}>
                      Loading {telemetryInterval} telemetry records...
                    </td>
                  </tr>
                ) : activeRawList.length > 0 ? (
                  activeRawList.map((r, i) => (
                    <tr key={i}>
                      <td>{r.startTime}</td>
                      <td>{r.opening}</td>
                      <td>{r.endTime}</td>
                      <td>{r.closing}</td>
                      <td>{r.net}</td>
                      <td>
                        <span
                          className={r.pump === 'Active' ? 'cell-badge live' : 'cell-badge idle'}
                          style={r.pump === 'Active' ? {} : { background: '#f1f5f9', color: '#64748b' }}
                        >
                          {r.pump}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" style={{ textAlign: 'center', color: '#8aa1ad', padding: '24px' }}>
                      No telemetry records found for selected interval
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {totalRawItems > 0 && (
            <div className="tbl-foot">
              <span>
                {(rawPage - 1) * rawPageSize + 1}–{Math.min(rawPage * rawPageSize, totalRawItems)} of{' '}
                {totalRawItems}
              </span>
              <div className="pager">
                <button disabled={rawPage <= 1} onClick={() => setRawPage((p) => Math.max(1, p - 1))}>
                  ‹
                </button>
                <span>
                  Page {rawPage} of {totalRawPages}
                </span>
                <button disabled={rawPage >= totalRawPages} onClick={() => setRawPage((p) => Math.min(totalRawPages, p + 1))}>
                  ›
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


